import React, { useState, useEffect } from "react";
import {
  questionnaireTotalization,
  answer,
  questionnaireAnswer,
  questionnaire,
} from "../../types/pages/Team/detail";
import { calcPostedTime } from "../../utils/TextUtil";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

type Props = {
  questionnaire_totalizations: Array<questionnaireTotalization>;
  questionnaire_answer: questionnaireAnswer;
  questionnaire: questionnaire;
};

const Message: React.FC<Props> = (props) => {
  const { questionnaire_totalizations, questionnaire_answer, questionnaire } =
    props;
  const [chartLabel, setChartLabel] = useState<Array<string>>();
  const [chartData, setChartData] = useState<Array<number>>();

  useEffect(() => {
    generateChart();
  }, []);

  const generateChart = () => {
    const totalizations = questionnaire_totalizations;
    const label = [];
    const data = [];

    totalizations.sort((a, b) => {
      if (a.order_num > b.order_num) {
        return 1;
      } else {
        return -1;
      }
    });

    totalizations.forEach((item: questionnaireTotalization) => {
      if (window.screen.width <= 414) {
        const string = item.questionnaire_option.sentence.length > 10 ? item.questionnaire_option.sentence.slice(0, 10) + "..." : item.questionnaire_option.sentence
        label.push(string);
      } else if (window.screen.width < 768) {
        const string = item.questionnaire_option.sentence.length > 16 ? item.questionnaire_option.sentence.slice(0, 16) + "..." : item.questionnaire_option.sentence
        label.push(string);
      } else {
        label.push(item.questionnaire_option.sentence)
      }
      data.push(item.total_count);
    });

    if (label.length > 10) {
      const canvas: HTMLElement = document.querySelector("#questionnaire-canvas")
      canvas.style.height = `${20 * label.length}px`
    }

    setChartLabel(label);
    setChartData(data);
  };

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );

  /**
   * 文字列の表示幅の計算 (半角=1, 全角=2)
   * @param str 対象の文字列
   * @returns 文字列の表示幅
  */
  const getStringWidth = (str: string): number => {
    let width = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      // 印刷可能なASCII文字と半角カタカナ以外を全角としてカウント
      if (char.match(/[^\x20-\x7E\uFF61-\uFF9F]/u)) {
        width += 2;
      } else {
        width += 1;
      }
    }
    return width;
  };

  /**
   * 文字列を指定された最大表示幅で分割
   * @param str 分割対象の文字列
   * @param maxWidth 1行あたりの最大表示幅 (半角文字換算)
   * @returns 分割された文字列の配列
  */
  const splitStringByWidth = (str: string, maxWidth: number): string[] => {
    const result: string[] = [];
    let currentLine = "";
    let currentWidth = 0;

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const charWidth = getStringWidth(char); // 1文字の幅(1 or 2)

      // 次の文字を追加すると最大幅を超える場合は、現在の行を確定させる
      if (currentWidth + charWidth > maxWidth) {
        result.push(currentLine);
        currentLine = "";
        currentWidth = 0;
      }

      currentLine += char;
      currentWidth += charWidth;
    }
    
    // 最後の行を追加
    if (currentLine) {
      result.push(currentLine);
    }

    return result;
  };

  /**
   * 文字列に含まれる全角・半角文字の数を数える
   * @param str 対象の文字列
   * @returns { full: number, half: number }
   */
  const countCharacterTypes = (str: string): { full: number, half: number } => {
    let fullWidthCount = 0;
    let halfWidthCount = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      if (char.match(/[^\x20-\x7E\uFF61-\uFF9F]/u)) {
        fullWidthCount++;
      } else {
        halfWidthCount++;
      }
    }
    return { full: fullWidthCount, half: halfWidthCount };
  };

  const options = {
    maintainAspectRatio: false,
    barThickness: 4,
    indexAxis: "y" as const,
    elements: {
      bar: {
        borderWidth: 1,
      },
    },
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: "right" as const,
      },
      label: {
        display: false,
      },
    },
    scales: {
      y: {
        ticks: {
          textAlign: "left",
          autoSkip: false,
          font: {
            family: "'Courier New', Courier, monospace",
          },
          callback: function (value: string | number) {
            const label = chartLabel[value as number] || '';
            const maxLength = 30;
          
            if (getStringWidth(label) > maxLength) {
              const lines = splitStringByWidth(label, maxLength);

              if (lines.length < 2) {
                return lines;
              }

              // --- パディング生成フェーズ ---
              // 最後の行と、その1つ上の行で左寄せになるように調整する
              const lastLine = lines[lines.length - 1];
              const lastLineCounts = countCharacterTypes(lastLine);
              const penultimateLine = lines[lines.length - 2];
              const penultimateLineCounts = countCharacterTypes(penultimateLine);

              let fullWidthDeficit = penultimateLineCounts.full - lastLineCounts.full;
              let halfWidthDeficit = penultimateLineCounts.half - lastLineCounts.half;

              // 1. 全角文字が過剰 (deficitがマイナス) の場合
              if (fullWidthDeficit < 0) {
                // 過剰な全角文字の幅(x2)を、半角文字の不足数に加算（マイナスなので結果的に引き算になる）
                halfWidthDeficit += (fullWidthDeficit * 2);
                // 計算に使ったので、全角の不足数は0にする
                fullWidthDeficit = 0;
              }

              // 2. 半角文字が過剰 (deficitがマイナス) の場合
              if (halfWidthDeficit < 0) {
                // 過剰な半角文字の幅を、全角文字の不足数に加算
                // 全角文字は半角の2倍なので、過剰分を2で割ってから加算
                fullWidthDeficit += Math.floor(halfWidthDeficit / 2);
                // 2で割り切れなかった余り(0 or -1)だけを残す
                halfWidthDeficit = halfWidthDeficit % 2;
              }

              let padding = '';
              if (fullWidthDeficit > 0) {
                padding += '　'.repeat(fullWidthDeficit);
              }
              if (halfWidthDeficit > 0) {
                padding += ' '.repeat(halfWidthDeficit);
              }
              if (padding) {
                lines[lines.length - 1] += padding;
              }
              
              return lines;
            }
            
            return label;
          },
        }
      }
    }
  };

  const data = {
    labels: chartLabel,
    datasets: [
      {
        label: "票数",
        data: chartData,
        borderColor: ["rgba(31,33,34,1)"],
        backgroundColor: ["rgba(31,33,34,1)"],
      },
    ],
  };
  return (
    <div>
      <div>
        <div className="text-[22px] mb-[50px] font-bold">
          《{new Date().getMonth() + 1}月{new Date().getDate() - 1}
          日時点の投票結果》
        </div>
        <div className="mb-10">
          <div className="bg-[#1F2122] w-[90px] h-[25px] flex items-center justify-center mb-1 text-white font-raleway text-sm rounded-default">
            Question1
          </div>
          <p className="text-xl font-bold">{questionnaire.title}</p>
        </div>
        <div className="bg-white p-[10px]">
          <Bar options={options} data={data} className="mt-2" id="questionnaire-canvas"/>
        </div>
      </div>
      {questionnaire.free_text_title && (
        <div className="mt-[80px]">
          <div className="mb-10">
            <div className="bg-[#1F2122] w-[90px] h-[25px] flex items-center justify-center mb-1 text-white font-raleway text-sm rounded-default">
              Question2
            </div>
            <p className="text-xl font-bold">{questionnaire.free_text_title}</p>
          </div>
          <ul className="p-10 pt-5 bg-white">
            {questionnaire_answer.answer.map((item: answer, index: number) => (
              <li
                key={index}
                className="border-[#D2DFE6] p-[30px] border-b sm:px-0 sm:py-5"
              >
                <div>
                  <div className="text-[#768084] mb-1 text-xs">
                    {item.account.user_name}
                  </div>
                  <p className="mb-[10px]">{item.sentence}</p>
                  <div className="flex items-center justify-between">
                    <div className="left-icon icon-clock text-[#768084] flex items-center text-xs">
                      {calcPostedTime(item.updated_at)}前
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Message;
